import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../../AuthProvider";
import Spinner from "../../components/Spinner";
import ErrorPage from "../../components/ErrorPage";
import { useTranslation } from "react-i18next";
import { mapHttpErrorToMessage } from "../../utils/mapHttpErrorToMessage";
import http from "../../api/apiClient";
import Modal from "../../components/Modal";
import type { PermissionCapability, PermissionCatalogItem, PermissionEntry, PermissionGroup, PermissionScope, Role } from "./types";
import { isUnusedRole } from "./roleUsage";
import {
  actionScopes,
  capabilityKey,
  dependencyLabel,
  oneOfChoiceId,
  parseCapabilityKey,
  persistedPermissionKeys,
  resolvePermissionDependencies,
  semanticsFor,
  type OneOfChoices,
} from "./permissionDependencies";
import RolePermissionsStyles from "./components/RolePermissionsStyles";
import { LdsOverviewHero, LdsOverviewPage } from "../../components/lds/LdsOverview";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../../components/lds/LdsTable";
import { FaPlus, FaShieldAlt } from "react-icons/fa";

/**
 * Resources that staff use with scope ANY and parents use in the parent portal with scope RELATED. Their RELATED
 * actions get a row of their own in the Parents group, so the parent role can be given or denied them separately.
 */
const PARENT_PORTAL_SPLIT_RESOURCES = new Set(["outing.info"]);

const CATALOG_SUPPLEMENTS: PermissionCatalogItem[] = [
  // Financieel: always listed, also before any role has these permissions.
  {
    group: "admin",
    resource: "finance.root",
    actions: ["view"],
    scope: "ANY",
    title: "Financieel",
    label: "Financieel",
  },
  {
    group: "admin",
    resource: "finance.contributions",
    actions: ["view", "manage"],
    scope: "ANY",
    title: "Financieel • Ouderbijdragen",
    label: "Financieel • Ouderbijdragen",
  },
  {
    group: "admin",
    resource: "finance.contributions.adjustments",
    actions: ["manage"],
    scope: "ANY",
    title: "Financieel • Afwijkende bijdrage",
    label: "Financieel • Afwijkende bijdrage",
  },
  {
    group: "admin",
    resource: "finance.reports.students",
    actions: ["view", "export"],
    scope: "ANY",
    title: "Financieel • Kinderen en ouders",
    label: "Financieel • Kinderen en ouders",
  },
  {
    group: "admin",
    resource: "finance.settings",
    actions: ["view", "manage"],
    scope: "ANY",
    title: "Financieel • Instellingen e-Boekhouden",
    label: "Financieel • Instellingen e-Boekhouden",
  },
  {
    group: "staff",
    resource: "my.environment",
    actions: ["view"],
    scope: "ANY",
    title: "Mijn omgeving",
    label: "Mijn omgeving",
  },
  {
    group: "staff",
    resource: "my.classrooms",
    actions: ["view"],
    scope: "ANY",
    title: "Mijn klassen",
    label: "Mijn klassen",
  },
  {
    group: "staff",
    resource: "my.parent-meetings",
    actions: ["view", "manage"],
    scope: "ANY",
    title: "Oudergesprekken",
    label: "Oudergesprekken",
  },
  {
    group: "staff",
    resource: "my.agenda-activities",
    actions: ["view"],
    scope: "ANY",
    title: "Agenda & activiteiten",
    label: "Agenda & activiteiten",
  },
  {
    group: "staff",
    resource: "classroom.calendar",
    actions: ["read", "manage"],
    scope: "ANY",
    title: "Klassenagenda",
    label: "Klassenagenda",
  },
  {
    group: "staff",
    resource: "outing.info",
    actions: ["read", "manage"],
    scope: "ANY",
    title: "Uitjes & formulieren",
    label: "Uitjes & formulieren",
  },
  {
    group: "parent",
    resource: "outing.info",
    actions: ["read", "update"],
    scope: "RELATED",
    title: "Ouderportaal • Uitjes en toestemming",
    label: "Ouderportaal • Uitjes en toestemming",
  },
  {
    group: "staff",
    resource: "school.placement-overview",
    actions: ["view"],
    scope: "ANY",
    title: "Plaatsing · Overzicht",
    label: "Plaatsing · Overzicht",
  },
  {
    group: "staff",
    resource: "school.future-planning",
    actions: ["view", "manage"],
    scope: "ANY",
    title: "Plaatsing · Toekomstige planning",
    label: "Plaatsing · Toekomstige planning",
  },
  {
    group: "admin",
    resource: "families",
    actions: ["list", "read", "crud", "manage"],
    scope: "ANY",
    title: "Family • Families",
    label: "Family • Families",
  },
  {
    group: "admin",
    resource: "family.parents",
    actions: ["list", "read", "crud", "manage", "delete"],
    scope: "ANY",
    title: "Family • Parents",
    label: "Family • Parents",
  },
  {
    group: "admin",
    resource: "family.children",
    actions: ["list", "read", "crud", "manage", "delete"],
    scope: "ANY",
    title: "Family • Children",
    label: "Family • Children",
  },
  {
    group: "admin",
    resource: "families.purge",
    actions: ["purge"],
    scope: "ANY",
    title: "Purge families",
    label: "Purge families",
  },
  {
    group: "admin",
    resource: "development.activities",
    actions: ["read", "delete"],
    scope: "ANY",
    title: "Development • Activities",
    label: "Development • Activities",
    description: "Development activity sidebar and activity API access.",
    actionDescriptions: {
      read: "Development activity sidebar and activity API access.",
      delete: "Clear development activity entries.",
    },
  },
  {
    group: "administration",
    resource: "administration.mutation-processing",
    actions: ["read", "update"],
    scope: "ANY",
    title: "Administration • Mutation processing",
    label: "Administration • Mutation processing",
    description: "View and change automatic mutation processing configuration.",
    actionDescriptions: {
      read: "View mutation processing configuration.",
      update: "Change automatic mutation processing configuration.",
    },
  },
  {
    group: "admin",
    resource: "school.teachers",
    actions: ["list", "read", "crud", "manage", "linkSelf"],
    scope: "ANY",
    title: "School • Teachers",
    label: "School • Teachers",
  },
];

const extractCatalogItems = (data: any): PermissionCatalogItem[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data as PermissionCatalogItem[];

  const fromObject = [
    data.items,
    data.entries,
    data.permissions,
    data.catalog,
    data.resources,
  ].find(Array.isArray) as any[] | undefined;
  if (fromObject) return fromObject as PermissionCatalogItem[];

  if (Array.isArray(data.groups)) {
    const flattened: PermissionCatalogItem[] = [];
    data.groups.forEach((group: any) => {
      const entries = Array.isArray(group?.entries)
        ? group.entries
        : Array.isArray(group?.items)
          ? group.items
          : [];
      entries.forEach((entry: any) => flattened.push(entry));
    });
    return flattened;
  }

  return [];
};


const RESOURCE_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  "profile.self": "Own profile",
  admin: "Beheer",
  "admin.notifications": "Beheer • Notificaties",
  "admin.users": "Beheer • Gebruikers",
  "admin.backups": "Beheer • Back-ups",
  "admin.audit": "Beheer • Audit / logging",
  "development.activities": "Development • Activities",
  alumni: "Administratie • Alumni",
  attendance: "Attendance (mark)",
  directory: "Tools • Directory",
  family: "Family",
  "family.directory": "Tools • Directory access",
  families: "Families • Families",
  "families.bankaccounts": "Tools • Bankrekening opzoeken",
  "families.purge": "Families • Opschonen",
  "family.children": "Family • Children",
  "family.parents": "Family • Parents",
  "integration.noctis.object-types": "Integration • Noctis",
  "newsletter.subscriptions": "Newsletter",
  "my.environment": "Mijn omgeving",
  "my.classrooms": "Mijn klassen",
  "my.parent-meetings": "Oudergesprekken",
  "my.agenda-activities": "Agenda & activiteiten",
  "classroom.calendar": "Klassenagenda",
  "outing.info": "Uitjes & formulieren",
  "reports.root": "Reports (root)",
  "reports.students": "Reports • Students",
  "reports.attendance": "Reports • Attendance",
  "reports.alumni": "Legacy • Alumni export",
  "reports.privacy": "Reports • Privacy",
  "reports.builder": "Rapporten • Rapportbouwer",
  "reports.unassigned-children": "Rapporten • Niet-toegewezen kinderen (onderliggend)",
  "signals.unassigned-children": "Signaleringen • Niet-toegewezen kinderen",
  "signals.waiting-list-conflicts": "Signaleringen • Interesselijst-conflicten",
  "signals.current-school-year": "Signaleringen • Controle huidig schooljaar",
  "administration.mutation-processing": "Administratie • Mutatieverwerking",
  school: "School",
  "school.applications": "Plaatsing • Aanmeldingen",
  "school.attendance": "School • Attendance",
  "school.classrooms": "School • Classrooms",
  "school.classrooms.overview": "Plaatsing • Klassenoverzicht",
  "school.links": "School • Schoolbrede links",
  "school.placement-overview": "Plaatsing · Overzicht",
  "school.future-planning": "Plaatsing · Toekomstige planning",
  "school.mutations": "Administratie • Mutatieverzoeken",
  "school.teachers": "School • Teachers",
  "school.waiting-list": "Plaatsing • Interesselijst",
  "school.years": "School • School years",
  "children.bsn": "Children • BSN",
  "finance.root": "Financieel",
  "finance.contributions": "Financieel • Ouderbijdragen",
  "finance.contributions.adjustments": "Financieel • Afwijkende bijdrage",
  "finance.reports.students": "Financieel • Kinderen en ouders",
  "finance.settings": "Financieel • Instellingen e-Boekhouden",
  "parent.contributions": "Ouderportaal • Ouderbijdrage",
  "outing.info|RELATED": "Ouderportaal • Uitjes en toestemming",
};

const PERMISSION_ACTION_LABELS: Record<string, string> = {
  "my.environment|view": "Mijn omgeving bekijken",
  "my.classrooms|view": "Mijn klassen bekijken",
  "my.parent-meetings|view": "Oudergesprekken bekijken",
  "my.parent-meetings|manage": "Oudergesprekken beheren",
  "my.agenda-activities|view": "Agenda & activiteiten bekijken",
  "classroom.calendar|read": "Klassenagenda bekijken",
  "classroom.calendar|manage": "Klassenagenda beheren",
  "school.links|manage": "Schoolbrede links beheren",
  "outing.info|read": "Uitjes/formulieren bekijken",
  "outing.info|manage": "Uitjes/formulieren beheren",
  "outing.info|read|RELATED": "Formulieren van eigen kinderen zien",
  "outing.info|update|RELATED": "Toestemmingsformulier invullen",
  "school.placement-overview|view": "Plaatsingsoverzicht bekijken",
  "school.future-planning|view": "Toekomstige planning bekijken",
  "school.future-planning|manage": "Toekomstige planning beheren",
  "administration.mutation-processing|read": "Mutatieverwerking bekijken",
  "administration.mutation-processing|update": "Mutatieverwerking wijzigen",
  "finance.root|view": "Menu Financieel zien",
  "finance.contributions|view": "Ouderbijdragen en betalingen bekijken",
  "finance.contributions|manage": "Betalingen ophalen, koppelen en vastleggen; openzetten voor ouders",
  "finance.contributions.adjustments|manage": "Afwijkende bijdrage per kind instellen",
  "finance.reports.students|view": "Kinderen en ouders bekijken",
  "finance.reports.students|export": "Kinderen en ouders exporteren",
  "finance.settings|view": "Koppeling e-Boekhouden bekijken",
  "finance.settings|manage": "Koppeling e-Boekhouden instellen",
  "parent.contributions|view": "Eigen ouderbijdrage zien (als Financieel die heeft opengezet)",
  "reports.builder|view": "Bibliotheek lezen: gedeelde rapporten openen en filteren",
  "reports.builder|create": "Rapportbouwer: eigen rapporten maken en bewaren",
  "reports.builder|export": "Rapporten exporteren naar Excel en afdrukken",
  "reports.builder|share": "Eigen rapporten delen in de bibliotheek",
  "reports.builder|manage": "Rapporten van anderen wijzigen en verwijderen",
  "development.activities|read": "Read",
  "development.activities|delete": "Delete",
};

const permissionActionLabel = (resource: string, action: string, scope?: string) =>
  (scope && PERMISSION_ACTION_LABELS[`${resource}|${action}|${scope}`]) ||
  PERMISSION_ACTION_LABELS[`${resource}|${action}`] ||
  action;

const PLACEMENT_RESOURCE_ORDER: Record<string, number> = {
  "school.placement-overview": 1,
  "school.applications": 2,
  "school.classrooms.overview": 3,
  "school.waiting-list": 4,
  "school.future-planning": 5,
};

const DIRECTORY_PARENT_PORTAL_ACTIONS = new Set(["view", "edit"]);

const isParentPortalCatalogItem = (item: PermissionCatalogItem, action?: string) => {
  const resource = item.resource || "";
  const roleGroup = item.group || "";
  const scope = item.scope || "";
  const normalizedAction = action ? String(action).trim() : "";
  const itemActions = Array.isArray(item.actions)
    ? item.actions.map((a) => String(a).trim()).filter(Boolean)
    : item.action
      ? [String(item.action).trim()].filter(Boolean)
      : [];

  if (resource === "directory") {
    if (normalizedAction) return DIRECTORY_PARENT_PORTAL_ACTIONS.has(normalizedAction);
    return itemActions.length > 0 && itemActions.every((a) => DIRECTORY_PARENT_PORTAL_ACTIONS.has(a));
  }

  return (
    roleGroup === "parent" ||
    scope === "RELATED" ||
    resource.endsWith(".me") ||
    resource.includes(".mine") ||
    resource.startsWith("parents.me") ||
    resource.startsWith("parent.") ||
    resource.startsWith("families.me") ||
    resource.startsWith("mutation-requests") ||
    resource.startsWith("family.bank-accounts") ||
    resource.startsWith("bank-accounts")
  );
};

const groupTitle = (item: PermissionCatalogItem) => {
  const resource = item.resource || "";
  const key = resource.split(".")[0] || resource;

  // Ouderportaal heeft overlap met algemene resources.
  // Daarom eerst op group/scope afvangen, niet alleen op resource.
  if (isParentPortalCatalogItem(item)) {
    return "Parents";
  }

  if (
    resource === "school.applications" ||
    resource === "school.waiting-list" ||
    resource === "school.classrooms.overview" ||
    resource === "school.placement-overview" ||
    resource === "school.future-planning"
  ) {
    return "Plaatsing";
  }

  if (resource === "school.mutations" || resource === "administration.mutation-processing") {
    return "Administratie";
  }

  if (resource === "development.activities") {
    return "Beheer";
  }

  if (
    resource === "my.environment" ||
    resource === "my.classrooms" ||
    resource === "my.parent-meetings" ||
    resource === "my.agenda-activities"
  ) {
    return "Mijn omgeving";
  }

  if (resource === "classroom.calendar") {
    return "Agenda & activiteiten";
  }

  if (resource === "outing.info") {
    return "Uitjes & formulieren";
  }

  if (resource.startsWith("signals.")) {
    return "Signaleringen";
  }

  if (resource === "finance" || resource.startsWith("finance.")) {
    return "Financiën";
  }

  if (resource === "directory" || resource === "family.directory" || resource === "families.bankaccounts") {
    return "Tools";
  }

  switch (key) {
    case "*":
      return "Systeem";

    case "dashboard":
      return "Dashboard";

    case "family":
    case "families":
    case "children":
    case "parents":
      return "Families";

    case "school":
    case "teacher":
      return "School";

    case "attendance":
      return "Aanwezigheid";

    case "reports":
      return "Rapporten";

    case "admin":
    case "development":
      return "Beheer";

    case "integration":
    case "gitea":
    case "mailchimp":
      return "Integraties";

    case "newsletter":
      return "Nieuwsbrief";

    case "communication":
      return "Communicatie";

    case "privacy":
      return "Privacy";

    case "profile":
      return "Profiel";

    case "alumni":
      return "Administratie";

    case "outing":
      return "Uitjes";

    case "feature":
      return "Features";

    default:
      return "Overig";
  }
};

const Roles = () => {
  const { keycloak } = useContext(AuthContext);
  const { t } = useTranslation();
  const [roles, setRoles] = useState<Role[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showUnusedOnly, setShowUnusedOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [permissionCatalogItems, setPermissionCatalogItems] = useState<PermissionCatalogItem[] | null>(null);
  const [permissionCatalogLoading, setPermissionCatalogLoading] = useState(false);
  const [permissionCatalogError, setPermissionCatalogError] = useState<any>(null);
  const [showForm, setShowForm] = useState(false);
  const [newRole, setNewRole] = useState<{ name: string; description: string }>({
    name: "",
    description: "",
  });
  const [savingRole, setSavingRole] = useState(false);
  const [formError, setFormError] = useState("");
  const [feedback, setFeedback] = useState<any>(null);
  const [confirmModal, setConfirmModal] = useState<{ show: boolean; role: Role | null }>({
    show: false,
    role: null,
  });
  const [deletingRole, setDeletingRole] = useState("");
  const [viewedRole, setViewedRole] = useState<Role | null>(null);
  const [viewedPermissions, setViewedPermissions] = useState<Set<string>>(new Set());
  const [viewLoading, setViewLoading] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set<string>());
  const [oneOfChoices, setOneOfChoices] = useState<OneOfChoices>({});
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set<string>());
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [loadingRoleDetail, setLoadingRoleDetail] = useState(false);
  const hasFetchedOnceRef = useRef(false);

  const normalizeRole = useCallback((role: unknown): Role => {
    if (typeof role === "string") {
      return { id: role, name: role, description: "" };
    }
    if (!role || typeof role !== "object") {
      return { id: "", name: "", description: "" };
    }
    // @ts-expect-error loose api shape
    const name = role.name || role.roleName || role.id || "";
    return {
      // @ts-expect-error loose api shape
      id: role.id || name,
      name,
      // @ts-expect-error loose api shape
      description: role.description || role.roleDescription || "",
      // @ts-expect-error loose api shape
      permissionCount: typeof role.permissionCount === "number" ? role.permissionCount : null,
      // @ts-expect-error loose api shape
      hasMembers: typeof role.hasMembers === "boolean" ? role.hasMembers : null,
      // @ts-expect-error loose api shape
      keycloakDefault: role.keycloakDefault === true,
    };
  }, []);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/users/roles");
      const items: Role[] = Array.isArray(data) ? data.map(normalizeRole) : [];
      const sorted = items.sort((a, b) => a.name.localeCompare(b.name));
      setRoles(sorted);
      setError(null);
    } catch (err) {
      setError(mapHttpErrorToMessage(err, t));
    } finally {
      setLoading(false);
    }
  }, [normalizeRole, t]);

  const fetchPermissionCatalog = useCallback(async () => {
    setPermissionCatalogLoading(true);
    setPermissionCatalogError(null);
    try {
      const candidates = ["/admin/permissions/catalog", "/admin/users/roles/catalog"];
      let data: any = null;
      let lastErr: any = null;
      for (const url of candidates) {
        try {
          const res = await http.get(url);
          data = res?.data;
          lastErr = null;
          break;
        } catch (err) {
          lastErr = err;
        }
      }
      if (lastErr) throw lastErr;

      const items = extractCatalogItems(data);
      setPermissionCatalogItems(items);
    } catch (err) {
      setPermissionCatalogItems(null);
      setPermissionCatalogError(mapHttpErrorToMessage(err, t));
    } finally {
      setPermissionCatalogLoading(false);
    }
  }, [t]);

  const permissionCatalog: PermissionGroup[] = useMemo(() => {
    const groupedResources = new Map<string, PermissionEntry>();
    const catalogLabels = new Map<string, string>();

    const add = (item: PermissionCatalogItem, action: string) => {
      const resource = item?.resource ? String(item.resource).trim() : "";
      if (!resource || !action) return;

      const key = isParentPortalCatalogItem(item, action)
        ? `${item.group || ""}|${resource}|${item.scope || ""}`
        : resource;

      if (!groupedResources.has(key)) {
        groupedResources.set(key, {
          resource,
          actions: [],
          group: item.group,
          scope: item.scope,
          title: item.title,
          label: item.label,
          semantics: item.semantics,
          navigation: item.navigation,
        });
      }

      const entry = groupedResources.get(key)!;

      if (!entry.actions.includes(action)) {
        entry.actions.push(action);
      }

      entry.title ??= item.title;
      entry.label ??= item.label;
      // Merge, keeping what was there: split resources arrive one action at a time.
      entry.semantics = entry.semantics && item.semantics
        ? { ...item.semantics, ...entry.semantics }
        : entry.semantics ?? item.semantics;
      entry.navigation ??= item.navigation;
    };

    const catalogItems = [
      ...(Array.isArray(permissionCatalogItems) ? permissionCatalogItems : []),
      ...CATALOG_SUPPLEMENTS,
    ];

    if (catalogItems.length > 0) {
      catalogItems.forEach((item) => {
        const resource = item?.resource ? String(item.resource).trim() : "";
        if (!resource) return;
        const label = item?.title || item?.label;
        if (label && !catalogLabels.has(resource)) catalogLabels.set(resource, String(label));

        const actions = Array.isArray(item?.actions)
          ? item.actions
          : item?.action
            ? [item.action]
            : [];
        const splitForParents = PARENT_PORTAL_SPLIT_RESOURCES.has(resource) && item.group !== "parent";
        actions
          .map((a) => String(a).trim())
          .filter(Boolean)
          .forEach((action) => {
            if (!splitForParents) {
              add(item, action);
              return;
            }
            const semantics = item.semantics?.[action] || [];
            const related = semantics.filter((entry) => entry.scope === "RELATED");
            const other = semantics.filter((entry) => entry.scope !== "RELATED");
            if (related.length) {
              add({ ...item, group: "parent", scope: "RELATED", semantics: { [action]: related } }, action);
            }
            if (other.length || !related.length) {
              add({ ...item, semantics: other.length ? { [action]: other } : undefined }, action);
            }
          });
      });
    }

    const groups = new Map<string, PermissionGroup>();
    const compareResources = (a: string, b: string) => {
      const aa = String(a || "").trim();
      const bb = String(b || "").trim();
      const placementA = PLACEMENT_RESOURCE_ORDER[aa];
      const placementB = PLACEMENT_RESOURCE_ORDER[bb];
      if (placementA != null || placementB != null) {
        return (placementA ?? Number.MAX_SAFE_INTEGER) - (placementB ?? Number.MAX_SAFE_INTEGER);
      }
      if (aa === "*" && bb !== "*") return -1;
      if (bb === "*" && aa !== "*") return 1;
      return aa.localeCompare(bb, undefined, { numeric: true, sensitivity: "base" });
    };

    Array.from(groupedResources.values())
      // Sort by actual resource key for predictable ordering in UI (avoids title-based reordering).
      .sort((a, b) => compareResources(a.resource, b.resource))
      .forEach((entry) => {
        const gKey = groupTitle(entry);
        if (!groups.has(gKey)) {
          groups.set(gKey, { key: gKey, title: gKey, entries: [] });
        }
        groups.get(gKey)!.entries.push({
          ...entry,
          actions: entry.actions.sort((a, b) => a.localeCompare(b)),
        });
      });

    return Array.from(groups.values()).sort((a, b) => a.title.localeCompare(b.title));
  }, [permissionCatalogItems]);

  const dependencyResolution = useMemo(
    () => resolvePermissionDependencies(selectedPermissions, permissionCatalog, oneOfChoices),
    [oneOfChoices, permissionCatalog, selectedPermissions]
  );

  useEffect(() => {
    if (hasFetchedOnceRef.current) return;
    if (keycloak?.token) {
      hasFetchedOnceRef.current = true;
      fetchRoles();
      fetchPermissionCatalog();
    }
  }, [keycloak?.token, fetchRoles, fetchPermissionCatalog]);

  const handleRetry = () => {
    setError(null);
    fetchRoles();
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setNewRole((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    setFormError("");
    setFeedback(null);

    const trimmedName = newRole.name.trim();
    const trimmedDescription = newRole.description.trim();

    if (!trimmedName) {
      setFormError(
        t("admin.roles.validation_name_required", {
          defaultValue: "Role name is required.",
        })
      );
      return;
    }

    try {
      setSavingRole(true);
      const payload: any = {
        name: trimmedName,
        description: trimmedDescription || null,
      };
      if (selectedPermissionsPayload.length > 0) {
        payload.permissions = selectedPermissionsPayload;
      }

      const isEdit = !!editingRole;
      const key = editingRole ? roleKey(editingRole) : null;
      const { data } = isEdit && key
        ? await http.put(`/admin/users/roles/${encodeURIComponent(key)}`, payload)
        : await http.post("/admin/users/roles", payload);
      const created = normalizeRole(
        data || { id: key || trimmedName, name: trimmedName, description: trimmedDescription }
      );
      setRoles((prev) => {
        const filtered = prev.filter(
          (role) => role.id !== created.id && role.name.toLowerCase() !== created.name.toLowerCase()
        );
        return [...filtered, created].sort((a, b) => a.name.localeCompare(b.name));
      });
      setFeedback({
        type: "success",
        message: isEdit
          ? t("admin.roles.update_success", {
              defaultValue: "Role updated successfully.",
              role: created.name,
            })
          : t("admin.roles.create_success", {
              defaultValue: "Role created successfully.",
              role: created.name,
            }),
      });
      closeForm();
    } catch (err) {
      const mapped = mapHttpErrorToMessage(err, t);
      setFormError(mapped?.message || mapped?.title || t("error.error_message", "Something went wrong."));
    } finally {
      setSavingRole(false);
    }
  };

  const openDeleteModal = (role: Role) => {
    setConfirmModal({ show: true, role });
  };

  const closeDeleteModal = () => {
    setConfirmModal({ show: false, role: null });
  };

  const toPermissionKey = (p: { resource?: string; action?: string } | string) => {
    if (!p) return null;
    if (typeof p === "string") {
      const trimmed = p.trim();
      if (!trimmed) return null;
      const separators = [":", "|"];
      for (const sep of separators) {
        if (trimmed.includes(sep)) {
          const [res, act] = trimmed.split(sep);
          if (res && act) return `${res}|${act}`;
        }
      }
      const lastDot = trimmed.lastIndexOf(".");
      if (lastDot > 0 && lastDot < trimmed.length - 1) {
        const res = trimmed.slice(0, lastDot);
        const act = trimmed.slice(lastDot + 1);
        if (res && act) return `${res}|${act}`;
      }
      return null;
    }
    const res = p.resource || "";
    const act = p.action || "";
    if (!res || !act) return null;
    return `${res}|${act}`;
  };

  const toggleViewRole = async (role: Role) => {
    const key = roleKey(role);
    if (viewedRole && roleKey(viewedRole) === key) {
      setViewedRole(null);
      setViewedPermissions(new Set());
      setViewLoading(false);
      return;
    }
    setViewedRole(role);
    setViewedPermissions(new Set());
    setViewLoading(true);
    try {
      const { data } = await http.get(`/admin/users/roles/${encodeURIComponent(key)}`);
      const list = Array.isArray(data?.permissions) ? data.permissions : Array.isArray(data?.entries) ? data.entries : [];
      const keys = list
        .map((p: any) => toPermissionKey(p))
        .filter(Boolean) as string[];
      const unique = Array.from(new Set(keys));
      setViewedPermissions(new Set(unique));
    } catch (err) {
      setViewedPermissions(new Set());
    }
    setViewLoading(false);
  };

  const handleDeleteRole = async () => {
    const role = confirmModal.role;
    if (!role) return;

    const key = roleKey(role);
    setFeedback(null);
    setFormError("");
    setDeletingRole(key);
    try {
      await http.delete(`/admin/users/roles/${encodeURIComponent(key)}`);
      setRoles((prev) =>
        prev.filter(
          (item) =>
            item.id !== role.id &&
            item.name.toLowerCase() !== role.name.toLowerCase()
        )
      );
      setFeedback({
        type: "success",
        message: t("admin.roles.delete_success", {
          defaultValue: "Role \"{{role}}\" deleted.",
          role: role.name,
        }),
      });
    } catch (err) {
      const mapped = mapHttpErrorToMessage(err, t);
      setFeedback({
        type: "error",
        message:
          mapped?.message ||
          mapped?.title ||
          t("admin.roles.delete_failed", {
            defaultValue: "Failed to delete the role.",
            role: role.name,
          }),
      });
    } finally {
      setDeletingRole("");
      closeDeleteModal();
    }
  };

  const togglePermission = (resource: string, action: string, scope: PermissionScope) => {
    const key = capabilityKey({ resource, action, scope });
    setSelectedPermissions((prev: Set<string>) => {
      const next = new Set<string>(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleResourceAll = (entry: PermissionEntry) => {
    const keys = entry.actions.flatMap((action) => actionScopes(entry, action).map((scope) =>
      capabilityKey({ resource: entry.resource, action, scope })
    ));
    setSelectedPermissions((prev: Set<string>) => {
      const next = new Set<string>(prev);
      const allSelected = keys.every((key) => dependencyResolution.effective.has(key));
      keys.forEach((key) => {
        if (allSelected) {
          next.delete(key);
        } else {
          next.add(key);
        }
      });
      return next;
    });
  };

  const isPermissionSelected = (resource: string, action: string, scope: PermissionScope) =>
    dependencyResolution.effective.has(capabilityKey({ resource, action, scope }));

  const dependencySources = (resource: string, action: string, scope: PermissionScope) =>
    dependencyResolution.requiredBy.get(capabilityKey({ resource, action, scope }));

  const selectedPermissionsPayload = useMemo(
    () => {
      const entries: { resource: string; action: string; scope?: string }[] = [];
      const persisted = persistedPermissionKeys(selectedPermissions, dependencyResolution);
      persisted.forEach((key) => {
        const { resource, action, scope } = parseCapabilityKey(key);
        entries.push({ resource, action, scope });
      });
      return entries.sort((a, b) => `${a.resource}|${a.action}|${a.scope}`.localeCompare(`${b.resource}|${b.action}|${b.scope}`));
    },
    [dependencyResolution, selectedPermissions]
  );
  const roleKey = (role: Role) => role.id || role.name;

  const hydratePermissions = (perms: any[]) => {
    const keys = (Array.isArray(perms) ? perms : [])
      .map((p) => {
        if (!p) return null;
        if (typeof p === "string") {
          const trimmed = p.trim();
          if (!trimmed) return null;
          // allow "res:action", "res|action" or "res.action" (action = last segment)
          const separators = [":", "|"];
          for (const sep of separators) {
            if (trimmed.includes(sep)) {
              const [res, act, scope = "ANY"] = trimmed.split(sep);
              if (res && act) return capabilityKey({ resource: res, action: act, scope: scope as PermissionScope });
            }
          }
          const lastDot = trimmed.lastIndexOf(".");
          if (lastDot > 0 && lastDot < trimmed.length - 1) {
            const res = trimmed.slice(0, lastDot);
            const act = trimmed.slice(lastDot + 1);
            if (res && act) return capabilityKey({ resource: res, action: act, scope: "ANY" });
          }
          return null;
        }
        const res = p.resource || p.res || p.id || p.name;
        const act = p.action || p.act;
        if (!res || !act) return null;
        return capabilityKey({ resource: res, action: act, scope: (p.scope || "ANY") as PermissionScope });
      })
      .filter(Boolean) as string[];
    setSelectedPermissions(new Set<string>(keys));
  };

  const openCreateForm = () => {
    setShowForm(true);
    setEditingRole(null);
    setNewRole({ name: "", description: "" });
    setFormError("");
    setFeedback(null);
    setSelectedPermissions(new Set<string>());
    setOneOfChoices({});
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingRole(null);
    setNewRole({ name: "", description: "" });
    setSelectedPermissions(new Set<string>());
    setOneOfChoices({});
    setFormError("");
  };

  const openEditRole = async (role: Role) => {
    setEditingRole(role);
    setShowForm(true);
    setNewRole({ name: role.name, description: role.description || "" });
    setFormError("");
    setFeedback(null);
    setSelectedPermissions(new Set<string>());
    setOneOfChoices({});
    const key = roleKey(role);
    if (!key) return;
    setLoadingRoleDetail(true);
    try {
      const { data } = await http.get(`/admin/users/roles/${encodeURIComponent(key)}`);
      if (data?.permissions) {
        hydratePermissions(data.permissions);
      } else if (data?.entries) {
        hydratePermissions(data.entries);
      }
    } catch (err) {
      /* ignore detail load errors */
    } finally {
      setLoadingRoleDetail(false);
    }
  };

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const countSelectedInGroup = (group: PermissionGroup) => {
    const keys = group.entries.flatMap((entry) =>
      entry.actions.flatMap((action) => actionScopes(entry, action).map((scope) =>
        capabilityKey({ resource: entry.resource, action, scope })
      ))
    );
    const selected = keys.filter((k) => dependencyResolution.effective.has(k)).length;
    return { selected, total: keys.length };
  };

  const pageTitle = t("admin.roles.title", { defaultValue: "Roles" });
  const pageDescription = t("admin.roles.description", { defaultValue: "Manage all roles here." });
  const addRoleLabel = t("admin.roles.add_button", { defaultValue: "Add role" });
  const cancelLabel = t("common.actions.cancel", { defaultValue: "Cancel" });
  const nameLabel = t("admin.roles.name", { defaultValue: "Role name" });
  const descriptionLabel = t("admin.roles.description_label", { defaultValue: "Description" });
  const emptyLabel = t("admin.roles.empty", { defaultValue: "No roles found." });
  const descriptionPlaceholder = t("admin.roles.description_placeholder", { defaultValue: "Optional description" });
  const actionsLabel = t("common.actions.name", { defaultValue: "Actions" });
  const usageLabel = t("admin.roles.usage.label", { defaultValue: "Gebruik" });

  const renderRoleUsage = (role: Role) => {
    const badge = "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold";
    const neutral = `${badge} border-slate-200 bg-white/70 text-slate-600 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-300`;
    if (role.keycloakDefault) {
      return <span className={neutral}>{t("admin.roles.usage.keycloak_default", { defaultValue: "Keycloak-standaard" })}</span>;
    }
    if (isUnusedRole(role)) {
      return (
        <span className={`${badge} border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/50 dark:bg-amber-900/30 dark:text-amber-200`}>
          {t("admin.roles.usage.unused", { defaultValue: "Ongebruikt" })}
        </span>
      );
    }
    return (
      <div className="flex flex-wrap gap-1">
        {typeof role.permissionCount === "number" && (
          <span className={neutral}>
            {t("admin.roles.usage.permissions", { count: role.permissionCount, defaultValue: "{{count}} rechten" })}
          </span>
        )}
        {role.hasMembers != null && (
          <span className={neutral}>
            {role.hasMembers
              ? t("admin.roles.usage.has_members", { defaultValue: "Toegekend" })
              : t("admin.roles.usage.no_members", { defaultValue: "Niet toegekend" })}
          </span>
        )}
      </div>
    );
  };
  const deleteLabel = t("common.actions.delete", { defaultValue: "Delete" });
  const resourceLabel = useCallback(
    (resource: string, scope?: string) => {
      if (resource === "development.activities") {
        return t("admin.roles.permissions.development_activities.title", {
          defaultValue: "Development • Activities",
        });
      }
      return (scope && RESOURCE_LABELS[`${resource}|${scope}`]) || RESOURCE_LABELS[resource] || resource;
    },
    [t]
  );
  const actionLabel = useCallback(
    (resource: string, action: string, scope?: string) => {
      if (resource === "development.activities" && action === "read") {
        return t("admin.roles.permissions.development_activities.actions.read", {
          defaultValue: "Read",
        });
      }
      if (resource === "development.activities" && action === "delete") {
        return t("admin.roles.permissions.development_activities.actions.delete", {
          defaultValue: "Delete",
        });
      }
      return permissionActionLabel(resource, action, scope);
    },
    [t]
  );
  const confirmDeleteTitle = confirmModal.role
    ? t("admin.roles.confirm_delete_title", {
        defaultValue: "Delete role \"{{role}}\"",
        role: confirmModal.role.name,
      })
    : t("admin.roles.confirm_delete_generic", { defaultValue: "Delete role" });
  const confirmDeleteMessage = confirmModal.role
    ? t("admin.roles.confirm_delete", {
        defaultValue: "Are you sure you want to delete the role \"{{role}}\"? This action cannot be undone.",
        role: confirmModal.role.name,
      })
    : "";

  const buttonLabel = showForm ? cancelLabel : addRoleLabel;
  const handleTopButtonClick = showForm ? closeForm : openCreateForm;

  const feedbackClasses = !feedback
    ? ""
    : feedback.type === "success"
      ? "bg-green-50 border border-green-200 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300"
      : "bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300";

  const unusedRoleCount = useMemo(() => roles.filter(isUnusedRole).length, [roles]);

  const filteredRoles = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const candidates = showUnusedOnly ? roles.filter(isUnusedRole) : roles;
    if (!term) return candidates;
    return candidates.filter((role) => {
      const fields = [role.name, role.description].filter(Boolean).map((v) => v.toLowerCase());
      return fields.some((field) => field.includes(term));
    });
  }, [roles, searchTerm, showUnusedOnly]);

  if (loading) return <Spinner />;

  if (error) {
    return (
      <ErrorPage
        message={error}
        status={error?.status}
        onRetry={handleRetry}
      />
    );
  }

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaShieldAlt}
        eyebrow={t("navigation.management", { defaultValue: "Beheer" })}
        title={pageTitle}
        description={pageDescription}
        actions={
          <button
            type="button"
            onClick={handleTopButtonClick}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-5 py-3 text-sm font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200 dark:hover:bg-indigo-400/20"
          >
            {!showForm && <FaPlus aria-hidden="true" />}
            {buttonLabel}
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center rounded-full border border-indigo-100 bg-indigo-50 px-4 py-2 text-sm font-bold text-indigo-700 shadow-sm dark:border-indigo-500/30 dark:bg-indigo-900/40 dark:text-indigo-200">
          {filteredRoles.length} {t("admin.roles.count_label", { defaultValue: "roles" })}
        </span>
        {(unusedRoleCount > 0 || showUnusedOnly) && (
          <button
            type="button"
            aria-pressed={showUnusedOnly}
            onClick={() => setShowUnusedOnly((current) => !current)}
            className={`inline-flex items-center rounded-full border px-4 py-2 text-sm font-bold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-amber-400 ${
              showUnusedOnly
                ? "border-amber-400 bg-amber-100 text-amber-900 dark:border-amber-400/60 dark:bg-amber-500/30 dark:text-amber-100"
                : "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-500/30 dark:bg-amber-900/30 dark:text-amber-200"
            }`}
          >
            {showUnusedOnly
              ? t("admin.roles.usage.show_all", { defaultValue: "Toon alle rollen" })
              : t("admin.roles.usage.unused_filter", { count: unusedRoleCount, defaultValue: "{{count}} ongebruikt" })}
          </button>
        )}
      </div>

      {feedback && (
        <div className={`rounded-2xl px-4 py-3 text-sm ${feedbackClasses}`}>
          {feedback.message}
        </div>
      )}

      {showForm && (
        <form
          onSubmit={handleCreateRole}
          className="lds-overview-card-enter rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80 sm:p-6"
        >
          <div className="flex items-center gap-3">
            <div>
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                {editingRole
                  ? t("admin.roles.form_title_edit", { defaultValue: "Edit role" })
                  : t("admin.roles.form_title", { defaultValue: "Add a new role" })}
              </h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t("admin.roles.form_description", {
                  defaultValue: "Specify a unique role name. Descriptions help other admins understand its purpose.",
                })}
              </p>
            </div>
            {editingRole && (
              <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-100">
                {t("common.actions.editing", { defaultValue: "Editing" })}: {editingRole.name}
              </span>
            )}
          </div>
          {loadingRoleDetail && (
            <p className="mt-2 text-xs text-indigo-700 dark:text-indigo-200">
              {t("admin.roles.loading_permissions", { defaultValue: "Loading permissions…" })}
            </p>
          )}
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="role-name">
                {nameLabel}
              </label>
              <input
                id="role-name"
                name="name"
                value={newRole.name}
                onChange={handleFormChange}
                required
                className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500"
                placeholder={t("admin.roles.name_placeholder", { defaultValue: "e.g. teacher_manager" })}
                disabled={savingRole}
              />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="role-description">
                {descriptionLabel}
              </label>
              <input
                id="role-description"
                name="description"
                value={newRole.description}
                onChange={handleFormChange}
                className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500"
                placeholder={descriptionPlaceholder}
                disabled={savingRole}
              />
            </div>
          </div>
          <div className="mt-6 rounded-lg border border-indigo-100 bg-white p-4 text-sm shadow-sm dark:border-indigo-700/40 dark:bg-slate-900/60">
            <RolePermissionsStyles />
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h4 className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">
                  {t("admin.roles.permissions", { defaultValue: "Permissies voor deze rol" })}
                </h4>
                <p className="text-xs text-indigo-800/80 dark:text-indigo-200/80">
                  {t("admin.roles.permissions_hint", {
                    defaultValue: "Kies per onderdeel welke acties deze rol mag uitvoeren.",
                  })}
                </p>
                {permissionCatalogLoading && (
                  <p className="mt-1 text-xs text-indigo-700/80 dark:text-indigo-200/80">
                    {t("admin.roles.permissions_catalog_loading", { defaultValue: "Permissie-catalogus laden…" })}
                  </p>
                )}
                {!permissionCatalogLoading && permissionCatalogError && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-200">
                    {(permissionCatalogError?.message || permissionCatalogError?.title) ??
                      t("admin.roles.permissions_catalog_failed", {
                        defaultValue: "Kon permissie-catalogus niet laden; fallback lijst gebruikt.",
                      })}
                  </p>
                )}
              </div>
              {selectedPermissions.size > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPermissions(new Set<string>());
                    setOneOfChoices({});
                  }}
                  className="text-xs font-semibold text-indigo-700 underline hover:text-indigo-900 dark:text-indigo-200"
                >
                  {t("common.actions.clear", { defaultValue: "Alles leegmaken" })}
                </button>
              )}
            </div>
            <div className="space-y-2">
              {permissionCatalog.map((group) => {
                const { selected, total } = countSelectedInGroup(group);
                const expanded = expandedGroups.has(group.key);
                return (
                  <div
                    key={group.key}
                    className={`rounded-md border bg-gray-50/80 dark:bg-slate-900/50 ${
                      selected > 0
                        ? "border-indigo-200 shadow-[0_0_0_2px_rgba(99,102,241,0.18)] dark:border-indigo-600/60 dark:shadow-[0_0_0_2px_rgba(99,102,241,0.25)]"
                        : "border-gray-200 dark:border-gray-700"
                    }`}
                  >
                    <button
                      type="button"
                      className="flex w-full items-center justify-between px-3 py-2 text-left"
                      onClick={() => toggleGroup(group.key)}
                    >
                      <div className="flex flex-col">
                        <span
                          className={`text-sm font-semibold ${
                            selected > 0
                              ? "text-indigo-700 dark:text-indigo-200"
                              : "text-gray-900 dark:text-gray-100"
                          }`}
                        >
                          {group.title}
                        </span>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                          {selected}/{total} {t("common.actions.selected", { defaultValue: "geselecteerd" })}
                        </span>
                      </div>
                      <span className="text-xs text-indigo-700 dark:text-indigo-200">{expanded ? "▲" : "▼"}</span>
                    </button>
                    {expanded && (
                      <div className="border-t border-gray-200 px-3 pb-3 pt-2 dark:border-gray-700">
                        <div className="overflow-auto">
                          <table className="role-permissions-table min-w-full table-auto text-xs">
                            <thead>
                              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                <th className="py-1 pr-2">Resource</th>
                                <th className="py-1 pr-2">Acties</th>
                                <th className="py-1 text-right">Alles</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.entries.map((entry) => {
                                const friendly = resourceLabel(entry.resource, entry.scope);
                                const allSelected = entry.actions.every((action) => actionScopes(entry, action).every((scope) =>
                                  isPermissionSelected(entry.resource, action, scope)
                                ));
                                return (
                                  <tr key={entry.resource} className="border-t border-gray-100 dark:border-gray-800">
                                    <td className="py-2 pr-2 align-top">
                                      <div className="text-gray-900 dark:text-gray-100">{friendly}</div>
                                      <div className="text-[11px] text-gray-500 dark:text-gray-400">
                                        {entry.resource}
                                      </div>
                                    </td>
                                    <td className="py-2 pr-2 align-top">
                                      <div className="flex flex-wrap gap-2">
                                        {entry.actions.flatMap((action) => actionScopes(entry, action).map((scope) => {
                                          const permissionKey = capabilityKey({ resource: entry.resource, action, scope });
                                          const checked = isPermissionSelected(entry.resource, action, scope);
                                          const sources = dependencySources(entry.resource, action, scope);
                                          const isDerived = Boolean(sources?.size) && !selectedPermissions.has(permissionKey);
                                          const semantics = semanticsFor(entry, action, scope);
                                          return (
                                            <div key={permissionKey} className="rounded border border-gray-200 bg-white px-2 py-1 text-[11px] dark:border-gray-700 dark:bg-slate-800">
                                              <label className="flex items-center space-x-1">
                                                <input
                                                  type="checkbox"
                                                  className="h-4 w-4"
                                                  checked={checked}
                                                  disabled={isDerived}
                                                  onChange={() => togglePermission(entry.resource, action, scope)}
                                                  aria-describedby={isDerived ? `${permissionKey}-dependency` : undefined}
                                                />
                                                <span>{actionLabel(entry.resource, action, scope)}{scope !== "ANY" ? ` (${scope})` : ""}</span>
                                              </label>
                                              {isDerived && (
                                                <div id={`${permissionKey}-dependency`} className="mt-1 max-w-64 text-[10px] leading-tight text-indigo-700 dark:text-indigo-200">
                                                  {t("admin.roles.permissions_required_by", { defaultValue: "Required by {{permissions}}", permissions: Array.from(sources!).map(dependencyLabel).join(", ") })}
                                                </div>
                                              )}
                                              {checked && semantics?.oneOf?.map((alternatives, groupIndex) => {
                                                const choiceId = oneOfChoiceId(permissionKey, groupIndex);
                                                return (
                                                  <label key={choiceId} className="mt-2 block text-[10px] text-gray-600 dark:text-gray-300">
                                                    <span className="mb-1 block font-semibold">
                                                      {t("admin.roles.permissions_one_of", { defaultValue: "Choose one required permission" })}
                                                    </span>
                                                    <select
                                                      value={dependencyResolution.oneOfChoices[choiceId] || ""}
                                                      onChange={(event) => setOneOfChoices((previous) => ({ ...previous, [choiceId]: event.target.value }))}
                                                      className="max-w-full rounded border border-gray-300 bg-white px-1.5 py-1 text-[10px] dark:border-gray-600 dark:bg-slate-900"
                                                    >
                                                      {alternatives.map((alternative: PermissionCapability) => {
                                                        const alternativeKey = capabilityKey(alternative);
                                                        return <option key={alternativeKey} value={alternativeKey}>{dependencyLabel(alternativeKey)}</option>;
                                                      })}
                                                    </select>
                                                  </label>
                                                );
                                              })}
                                            </div>
                                          );
                                        }))}
                                      </div>
                                    </td>
                                    <td className="py-2 text-right align-top">
                                      <button
                                        type="button"
                                        onClick={() => toggleResourceAll(entry)}
                                        className="text-[11px] font-semibold text-indigo-700 underline hover:text-indigo-900 dark:text-indigo-200"
                                      >
                                        {allSelected
                                          ? t("admin.roles.permissions_deselect_all", { defaultValue: "Geen" })
                                          : t("admin.roles.permissions_select_all", { defaultValue: "Alles" })}
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {formError && (
            <p className="mt-3 text-sm text-red-600 dark:text-red-400">{formError}</p>
          )}
          <div className="mt-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={closeForm}
              className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-950 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
              disabled={savingRole}
            >
              {cancelLabel}
            </button>
            <button
              type="submit"
              disabled={savingRole}
              className="inline-flex items-center justify-center rounded-full border border-indigo-200 bg-indigo-50 px-6 py-2.5 text-sm font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-100 disabled:opacity-60 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200"
            >
              {savingRole
                ? t("common.loading", { defaultValue: "Loading…" })
                : editingRole
                  ? t("admin.roles.update_button", { defaultValue: "Update role" })
                  : t("admin.roles.create_button", { defaultValue: "Create role" })}
            </button>
          </div>
        </form>
      )}

      {!showForm && (
        <>
          <div className="lds-overview-card-enter rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("admin.roles.search_placeholder", { defaultValue: "Zoek rollen..." })}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500"
            />
          </div>

          <LdsTableCard className="lds-overview-card-enter overflow-hidden">
            <LdsTableScroll>
            {filteredRoles.length === 0 ? (
              <p className="p-6 text-sm text-gray-600 dark:text-gray-400">{emptyLabel}</p>
            ) : (
              <LdsTable>
                <LdsTableHead>
                  <LdsTableRow>
                    <LdsTableHeadCell>{nameLabel}</LdsTableHeadCell>
                    <LdsTableHeadCell>{descriptionLabel}</LdsTableHeadCell>
                    <LdsTableHeadCell>{usageLabel}</LdsTableHeadCell>
                    <LdsTableHeadCell>{actionsLabel}</LdsTableHeadCell>
                  </LdsTableRow>
                </LdsTableHead>
                <LdsTableBody>
                  {filteredRoles.map((role) => {
                    const key = role.id || role.name;
                    const isDeletingThis = deletingRole === key;
                    const isExpanded = viewedRole && roleKey(viewedRole) === key;
                    return (
                      <React.Fragment key={key}>
                        <LdsTableRow
                          className="cursor-pointer"
                          onClick={() => toggleViewRole(role)}
                        >
                          <LdsTableCell className="font-semibold text-slate-950 dark:text-white">{role.name}</LdsTableCell>
                          <LdsTableCell>{role.description || "—"}</LdsTableCell>
                          <LdsTableCell>{renderRoleUsage(role)}</LdsTableCell>
                          <LdsTableCell>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); toggleViewRole(role); }}
                                disabled={Boolean(deletingRole)}
                                className="lumen-glow-sheen rounded-full border border-slate-200/60 bg-white/70 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:text-indigo-900 disabled:opacity-60 dark:border-slate-500/40 dark:bg-slate-900/40 dark:text-slate-200"
                              >
                                {isExpanded
                                  ? t("common.actions.close", { defaultValue: "Close" })
                                  : t("common.actions.view", { defaultValue: "View" })}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openEditRole(role); }}
                                disabled={Boolean(deletingRole)}
                                className="lumen-glow-sheen rounded-full border border-indigo-100/60 bg-indigo-50/80 px-3 py-1.5 text-xs font-semibold text-indigo-700 transition hover:text-indigo-900 disabled:opacity-60 dark:border-indigo-500/40 dark:bg-indigo-900/40 dark:text-indigo-200"
                              >
                                {t("common.actions.edit", { defaultValue: "Edit" })}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeleteModal(role);
                                }}
                                disabled={Boolean(deletingRole)}
                                className="lumen-glow-sheen rounded-full border border-red-100/60 bg-red-50/80 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:text-red-700 disabled:opacity-60 dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-200"
                              >
                                {isDeletingThis
                                  ? t("common.loading", { defaultValue: "Loading…" })
                                  : deleteLabel}
                              </button>
                            </div>
                          </LdsTableCell>
                        </LdsTableRow>
                        {isExpanded && (
                          <LdsTableRow className="bg-indigo-50/40 hover:bg-indigo-50/40 dark:bg-indigo-950/20 dark:hover:bg-indigo-950/20">
                            <LdsTableCell colSpan={4} className="p-4">
                              <div className="space-y-3 rounded-xl border border-indigo-100/60 bg-white/90 p-4 shadow-sm dark:border-indigo-700/40 dark:bg-gray-900/70">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="text-xs uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                                      {t("common.actions.view", { defaultValue: "View" })}
                                    </div>
                                    <div className="text-lg font-semibold text-gray-900 dark:text-white">{role.name}</div>
                                    <div className="text-sm text-gray-600 dark:text-gray-300">
                                      {role.description || t("admin.roles.no_description", { defaultValue: "No description" })}
                                    </div>
                                  </div>
                                  {viewLoading && (
                                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                      <Spinner theme="parent" />
                                      <span>{t("common.loading", { defaultValue: "Loading…" })}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="grid gap-2 md:grid-cols-2">
                                  {permissionCatalog
                                    .map((group) => {
                                      const entries = group.entries
                                        .map((entry) => {
                                          const selectedActions = entry.actions.filter((act) =>
                                            viewedPermissions.has(`${entry.resource}|${act}`)
                                          );
                                          return selectedActions.length ? { ...entry, actions: selectedActions } : null;
                                        })
                                        .filter(Boolean) as PermissionEntry[];
                                      if (entries.length === 0) return null;
                                      return (
                                        <div
                                          key={group.key}
                                          className="rounded-xl border border-gray-200 bg-gray-50/80 p-2.5 dark:border-gray-700 dark:bg-slate-900/70"
                                        >
                                          <div className="mb-1 flex items-center justify-between">
                                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{group.title}</div>
                                            <div className="text-[11px] text-indigo-700 dark:text-indigo-200">
                                              {entries.reduce((acc, e) => acc + e.actions.length, 0)} perms
                                            </div>
                                          </div>
                                          <div className="space-y-1.5">
                                            {entries.map((entry) => (
                                              <div key={entry.resource} className="rounded-lg bg-white/90 px-2 py-1.5 dark:bg-slate-800/70">
                                                <div className="flex items-center justify-between">
                                                  <div>
                                                    <div className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">
                                                      {resourceLabel(entry.resource, entry.scope)}
                                                    </div>
                                                    <div className="text-[11px] text-gray-500 dark:text-gray-400">
                                                      {entry.resource}
                                                    </div>
                                                  </div>
                                                  <div className="flex flex-wrap justify-end gap-1">
                                                    {entry.actions.map((act) => {
                                                      const permKey = `${entry.resource}|${act}`;
                                                      return (
                                                        <span
                                                          key={permKey}
                                                          className="inline-flex items-center rounded-full border border-indigo-100 bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-100"
                                                        >
                                                          {actionLabel(entry.resource, act, entry.scope)}
                                                        </span>
                                                      );
                                                    })}
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      );
                                    })
                                    .filter(Boolean)}
                                </div>
                                {!viewLoading && viewedPermissions.size === 0 && (
                                  <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/70 p-3 text-sm text-gray-600 dark:border-gray-700 dark:bg-slate-900/40 dark:text-gray-300">
                                    {t("admin.roles.no_permissions", { defaultValue: "No permissions assigned." })}
                                  </div>
                                )}
                              </div>
                            </LdsTableCell>
                          </LdsTableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </LdsTableBody>
              </LdsTable>
            )}
            </LdsTableScroll>
          </LdsTableCard>
        </>
      )}
      <Modal
        show={confirmModal.show}
        type="confirm"
        title={confirmDeleteTitle}
        message={confirmDeleteMessage}
        onConfirm={handleDeleteRole}
        onClose={closeDeleteModal}
      />
    </LdsOverviewPage>
  );
};

export default Roles;
